import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDuration } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { useMediaForDancer } from '@/data/media';
import { useVideoEnabled } from '@/data/videoStorage';
import { VideoPlayer } from '@/components/media/VideoPlayer';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/dancer/videos')({
  component: DancerOwnVideos,
});

function useMyDancerRow() {
  return useQuery({
    queryKey: ['my-dancer-row'],
    queryFn: async () => {
      const u = await supabase.auth.getUser();
      const userId = u.data.user?.id;
      if (!userId) return null;
      const { data, error } = await supabase
        .from('dancers')
        .select('id, first_name, preferred_name')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useViewerName() {
  return useQuery({
    queryKey: ['viewer-name'],
    queryFn: async () => {
      const u = await supabase.auth.getUser();
      return u.data.user?.email ?? 'viewer';
    },
  });
}

function DancerOwnVideos() {
  const enabled = useVideoEnabled();
  const me = useMyDancerRow();
  const media = useMediaForDancer(enabled.data ? (me.data?.id ?? undefined) : undefined);
  const viewer = useViewerName();
  const watermark = viewer.data ? `${viewer.data} · ${new Date().toLocaleDateString()}` : undefined;

  if (enabled.isLoading || me.isLoading) return <p aria-live="polite">Loading...</p>;

  if (!enabled.data) {
    return (
      <Empty
        title="Videos are not enabled"
        description="Your studio has not enabled choreography videos yet."
      />
    );
  }

  if (!me.data) {
    return (
      <Empty
        title="Account not linked"
        description="Your dancer account isn't linked to a profile in this studio. Ask an admin."
      />
    );
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Your dances
        </p>
        <h1 className="mt-2">Videos</h1>
      </header>

      {media.isLoading && <p aria-live="polite">Loading...</p>}

      {!media.isLoading &&
        (media.data?.dances.length ?? 0) === 0 &&
        (media.data?.routines.length ?? 0) === 0 && (
          <Empty
            title="No videos yet"
            description="Choreography videos for your dances will appear here once uploaded."
          />
        )}

      {media.data?.dances.map((d) => (
        <section key={d.id}>
          <h2 className="text-[var(--text-xl)]">{d.name}</h2>
          <ul className="mt-3 grid gap-4 sm:grid-cols-2">
            {d.media.map((m) => (
              <li key={m.id} className="surface-elevated grid gap-3 p-4">
                <header>
                  <p className="font-medium">{m.title ?? '(untitled)'}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{m.kind}</Badge>
                    {m.duration_seconds != null && <> · {formatDuration(m.duration_seconds)}</>}
                  </p>
                </header>
                {m.kind === 'video' && <VideoPlayer assetId={m.id} watermark={watermark} />}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {media.data?.routines.map((r) => (
        <section key={r.id}>
          <h2 className="text-[var(--text-xl)]">
            {r.name} <span className="text-[var(--color-ink-muted)]">· competition</span>
          </h2>
          <ul className="mt-3 grid gap-4 sm:grid-cols-2">
            {r.media.map((m) => (
              <li key={m.id} className="surface-elevated grid gap-3 p-4">
                <header>
                  <p className="font-medium">{m.title ?? '(untitled)'}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{m.kind}</Badge>
                    {m.duration_seconds != null && <> · {formatDuration(m.duration_seconds)}</>}
                  </p>
                </header>
                {m.kind === 'video' && <VideoPlayer assetId={m.id} watermark={watermark} />}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
